/**
 * @file ForgotEmailVerification.js
 * @description Component for handling the email verification process during password recovery.
 */
import { useState, useEffect, useRef } from 'react';
import { useCompanyConfig } from '../../../hooks/useCompanyConfig';
import VerifyProfileForm from '../../onboarding/VerifyProfileForm';
import Spinner from '../../ui/spinners/Spinner';
import { useTheme } from '../../../store/ThemeContext';
import styles from '../AuthPage.module.scss';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import CompanyCodeForm from '../CompanyCodeForm';
import { useNavigate } from 'react-router-dom';
import Toast from '../../ui/toasts/Toast';
import HeightScaledContent from '../../ScaledContent/HeightScaledContent';
import PlaceholderLogo from '../../ui/PlaceholderLogo';
import { ROUTE_BUILDERS } from '../../../constants/routes';
import { getText } from '../../../i18n';
const ForgotEmailVerification = () => {
    const { isCompanyCodeReady, authData, setAuthData } = useCompanyConfig();
    const { companyCode } = authData;
    const themeStyles = useTheme();
    const [companyCodeError, setCompanyCodeError] = useState(null);
    const [verificationResult, setVerificationResult] = useState(null);
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [baseHeight, setBaseHeight] = useState(900);
    const contentRef = useRef(null);

    useEffect(() => {
        const checkMobileDevice = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobileDevice();
        window.addEventListener('resize', checkMobileDevice);
        return () => window.removeEventListener('resize', checkMobileDevice);
    }, []);

    const background = () => css`
        ${themeStyles?.loginBackground?.type === 'color'
            ? `background: ${themeStyles?.loginBackground?.color}`
            : `background-image: url(${themeStyles?.loginBackground?.image})`}
    `;

    const handleCompanyCodeSubmit = submittedCompanyCode => {
        const currentPath = window.location.pathname;
        const newUrl = `${currentPath}?companyCode=${submittedCompanyCode}`;
        window.location.href = newUrl;
    };

    const handleVerificationComplete = username => {
        Toast.success(getText('msg_5b8f8ed4c05d'));
        const loginUrl = new URL(
            ROUTE_BUILDERS.loginWithCompanyCode(companyCode),
            window.location.origin,
        );
        loginUrl.searchParams.set('username', username);
        navigate(
            `${loginUrl.pathname}${loginUrl.search}`,
        );
    };

    const handleBackToLogin = () => {
        navigate(ROUTE_BUILDERS.loginWithCompanyCode(companyCode));
    };

    useEffect(() => {
        const calculateContentHeight = () => {
            if (contentRef.current && !isMobile) {
                const height = contentRef.current.scrollHeight;
                if (height > 0) {
                    setBaseHeight(height);
                }
            }
        };

        calculateContentHeight();

        const timeoutId = setTimeout(calculateContentHeight, 100);

        return () => clearTimeout(timeoutId);
    }, [
        isCompanyCodeReady,
        authData.companyConfig,
        verificationResult,
        isMobile,
    ]);

    return (
        <div className={`${styles.container}`} css={background}>
            <HeightScaledContent baseHeight={baseHeight}>
                <div
                    ref={contentRef}
                    className={`${styles.authBox} col-12 col-sm-12 col-md-7 col-lg-5 m-auto`}
                >
                    <div
                        className={`${styles.errorContainer} ${
                            companyCodeError ? '' : 'invisible'
                        }`}
                    >
                        <p className="text-danger">{companyCodeError}</p>
                    </div>

                    {!isCompanyCodeReady ? (
                        <div className="text-center">
                            <Spinner
                                className={styles.loadingSpinner}
                                width={50}
                            />
                        </div>
                    ) : (
                        <div
                            className={`${!isMobile ? 'ing-card' : ''} ${styles.authContainer} card modal-content py-5`}
                        >
                            <small
                                className={`${styles.returnToLoginButton} position-absolute`}
                                onClick={handleBackToLogin}
                            >
                                <i className="bi bi-chevron-left ms-3" /> {getText('msg_b5cd32158b70')}
                            </small>

                            <div
                                className={`form-group text-center my-3  ${styles.logoContainer}`}
                            >
                                {themeStyles?.primaryLogo ? (
                                    <img
                                        src={themeStyles.primaryLogo}
                                        alt={getText('msg_5807dd602664')}
                                    />
                                ) : (
                                    <PlaceholderLogo />
                                )}
                            </div>

                            {!authData.companyConfig ? (
                                <CompanyCodeForm
                                    onSubmit={handleCompanyCodeSubmit}
                                    initialCompanyCode=""
                                />
                            ) : verificationResult ? (
                                <div
                                    className={`${styles.companyCodeForm} text-center px-5 mb-4 form-container p-3`}
                                >
                                    <h2 className="iNGAGED-Typography-Heading3 mb-3">
                                        {getText('msg_94dd9e08c129')} {verificationResult.fname}!
                                    </h2>
                                    <p className="text-muted mb-4">
                                        {getText('msg_5ff8f51e69e8')}
                                    </p>
                                    <h4 className="pt-4">
                                        {verificationResult.email}
                                    </h4>
                                    <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                                        <button
                                            className="ing-button-primary-left mx-auto mb-3 text-nowrap w-75 mb-5"
                                            onClick={handleBackToLogin}
                                        >
                                            <i className="bi bi-chevron-left"></i>
                                            {getText('msg_b5cd32158b70')}
                                        </button>
                                        <p>
                                            <i className="bi bi-envelope" />
                                            {' '}
                                            {getText('msg_97e2e9fd5798')}
                                            {' '}
                                            <a
                                                href={`/get-support${
                                                    companyCode
                                                        ? `?companyCode=${companyCode}`
                                                        : ''
                                                }`}
                                                className="pl-2"
                                            >
                                                {getText('msg_fe1a2922069b')}
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <VerifyProfileForm
                                    authData={{
                                        ...authData,
                                        ...(themeStyles.tenantId
                                            ? { tenantId: themeStyles.tenantId }
                                            : {}),
                                    }}
                                    isForgotEmail={true}
                                    onVerificationComplete={
                                        handleVerificationComplete
                                    }
                                />
                            )}
                        </div>
                    )}
                </div>
            </HeightScaledContent>
        </div>
    );
};

export default ForgotEmailVerification;
